import { SettingOutlined } from '@ant-design/icons';
import { Button, Popover, Segmented, Select, Tooltip, Typography } from 'antd';

import type { TimeFormat } from '@app/api/Branches/types';

import type { JournalTimeFormatOption } from '@pages/Calendar/hooks/useJournalTimeFormat';

import type {
  AppointmentPreviewField,
  CalendarGridStepMinutes,
  CalendarIntervalHeightSize,
} from '@pages/Calendar/data';
import { AppointmentPreviewSettings } from '@pages/Calendar/ui/AppointmentPreviewSettings';

import { useCalendarDisplaySettings } from '../hooks';

import type { FC } from 'react';

import './CalendarDisplaySettings.scss';

const { Text } = Typography;

interface CalendarDisplaySettingsOption<TValue> {
  label: string;
  value: TValue;
}

interface CalendarDisplaySettingsProps {
  tooltipTitle: string;
  testId: string;
  intervalHeightSize: CalendarIntervalHeightSize;
  intervalHeightSizeOptions: CalendarDisplaySettingsOption<CalendarIntervalHeightSize>[];
  gridStepMinutes: CalendarGridStepMinutes;
  gridStepMinuteOptions: CalendarDisplaySettingsOption<CalendarGridStepMinutes>[];
  preferredMaxColumns: number;
  preferredMaxColumnOptions: CalendarDisplaySettingsOption<number>[];
  timeFormat: TimeFormat | null;
  timeFormatOptions: JournalTimeFormatOption[];
  isTimeFormatLoading: boolean;
  onTimeFormatChange(value: TimeFormat): void;
  onIntervalHeightSizeChange(value: CalendarIntervalHeightSize): void;
  onGridStepMinutesChange(value: CalendarGridStepMinutes): void;
  onPreferredMaxColumnsChange(value: number): void;
  previewFields: AppointmentPreviewField[];
  onPreviewFieldsChange(value: AppointmentPreviewField[]): void;
}

export const CalendarDisplaySettings: FC<CalendarDisplaySettingsProps> = ({
  tooltipTitle,
  testId,
  intervalHeightSize,
  intervalHeightSizeOptions,
  gridStepMinutes,
  gridStepMinuteOptions,
  preferredMaxColumns,
  preferredMaxColumnOptions,
  timeFormat,
  timeFormatOptions,
  isTimeFormatLoading,
  onTimeFormatChange,
  onIntervalHeightSizeChange,
  onGridStepMinutesChange,
  onPreferredMaxColumnsChange,
  previewFields,
  onPreviewFieldsChange,
}) => {
  const { isOpen, openChangeHandler } = useCalendarDisplaySettings();

  const content = (
    <div className="CalendarDisplaySettings__content">
      <div className="CalendarDisplaySettings__group">
        <div className="CalendarDisplaySettings__group-header">
          <Text className="CalendarDisplaySettings__title">Высота секции</Text>
          <Text className="CalendarDisplaySettings__description">
            Управляет вертикальной плотностью календарной сетки.
          </Text>
        </div>

        <Segmented<CalendarIntervalHeightSize>
          size="small"
          block
          value={intervalHeightSize}
          options={intervalHeightSizeOptions}
          className="CalendarDisplaySettings__segmented"
          onChange={onIntervalHeightSizeChange}
        />
      </div>

      <div className="CalendarDisplaySettings__group">
        <div className="CalendarDisplaySettings__group-header">
          <Text className="CalendarDisplaySettings__title">
            Длительность секции
          </Text>
          <Text className="CalendarDisplaySettings__description">
            Определяет шаг сетки для клика, наведения и перетаскивания.
          </Text>
        </div>

        <Select<CalendarGridStepMinutes>
          size="middle"
          value={gridStepMinutes}
          options={gridStepMinuteOptions}
          className="CalendarDisplaySettings__select"
          onChange={onGridStepMinutesChange}
        />
      </div>

      <div className="CalendarDisplaySettings__group">
        <div className="CalendarDisplaySettings__group-header">
          <Text className="CalendarDisplaySettings__title">
            Максимум колонок
          </Text>
          <Text className="CalendarDisplaySettings__description">
            Предпочитаемый максимум; фактическое число зависит от ширины окна.
          </Text>
        </div>

        <Select<number>
          size="middle"
          value={preferredMaxColumns}
          options={preferredMaxColumnOptions}
          className="CalendarDisplaySettings__select"
          onChange={onPreferredMaxColumnsChange}
        />
      </div>

      <div className="CalendarDisplaySettings__group">
        <div className="CalendarDisplaySettings__group-header">
          <Text className="CalendarDisplaySettings__title">Формат времени</Text>
          <Text className="CalendarDisplaySettings__description">
            Та же настройка филиала, что на странице Настройки журнала записей.
          </Text>
        </div>

        <Select<TimeFormat>
          size="middle"
          value={timeFormat}
          options={timeFormatOptions}
          className="CalendarDisplaySettings__select"
          loading={isTimeFormatLoading}
          disabled={isTimeFormatLoading}
          placeholder="Выберите формат времени"
          onChange={onTimeFormatChange}
        />
      </div>

      <div className="CalendarDisplaySettings__group">
        <div className="CalendarDisplaySettings__group-header">
          <Text className="CalendarDisplaySettings__title">Превью записи</Text>
          <Text className="CalendarDisplaySettings__description">
            Какие строки показывать в записи в сетке и в каком порядке.
          </Text>
        </div>

        <AppointmentPreviewSettings
          fields={previewFields}
          onFieldsChange={onPreviewFieldsChange}
        />
      </div>
    </div>
  );

  return (
    <div className="CalendarDisplaySettings">
      <Popover
        trigger="click"
        placement="bottomRight"
        open={isOpen}
        content={content}
        classNames={{ root: 'CalendarDisplaySettings__popover' }}
        onOpenChange={openChangeHandler}
      >
        <Tooltip title={tooltipTitle}>
          <Button
            icon={<SettingOutlined />}
            className="CalendarDisplaySettings__trigger"
            data-testid={testId}
            aria-label={tooltipTitle}
          />
        </Tooltip>
      </Popover>
    </div>
  );
};
